<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router/composables";

import { TuFeedbackBanner } from "@tinyurldev/tu-ui-vue";

import useBillingPeriod from "@Composables/useBillingPeriod";
import useLegacyStore from "@Composables/useLegacyStore";
import useLimit from "@Composables/useLimit";
import useTranslation from "@Composables/useTranslation";

import LIMITS_TYPE from "@Constants/limits-type";
import { OVERAGE_STATUS } from "@Constants/overage-status";

import { closePagePopover, closeStaticPopover } from "@Helpers/functions/closePopover";
import { toLongDate } from "@Helpers/support";

const emits = defineEmits(["manage-limits"]);

const { billingPeriodMonthEndsAt } = useBillingPeriod();
const { getters } = useLegacyStore();
const linksLimit = useLimit(LIMITS_TYPE.LINKS_PER_MONTH);
const router = useRouter();
const { __ } = useTranslation();

const isUserSubscribed = computed(() => getters.isUserSubscribed);

const bannerContent = computed(() => {
  const resetDate = toLongDate(billingPeriodMonthEndsAt.value, "MMM DD, YYYY");

  const startingContent = linksLimit.isHardLimitReached.value
    ? __("links_limit_banner.hard_limits_reached")
    : __("links_limit_banner.soft_limits_reached");

  let endingContent = "";

  if (linksLimit.hasStatus(OVERAGE_STATUS.SYSTEM_ENABLE)) {
    const turnOnToUnlockMore = __("links_limit_banner.turn_on_overages");
    const upgradeToUnlockMore = __("links_limit_banner.upgrade_to_unlock");

    endingContent = getters.getUserCurrentSubscription?.isLegacy
      ? upgradeToUnlockMore
      : turnOnToUnlockMore;
  }

  return `${startingContent} ${resetDate}. ${endingContent}`;
});

const bannerButton = computed(() => {
  let buttonLabel = __("links_limit_banner.manage_label");

  if (
    linksLimit.limitStatus.value == OVERAGE_STATUS.ALL_DISABLE ||
    linksLimit.limitStatus.value == OVERAGE_STATUS.BILLABLE_ENABLE
  ) {
    buttonLabel = __("links_limit_banner.go_to_invoices");
  }

  return { label: buttonLabel };
});

function onManageLimits() {
  let redirectPath = { name: "settings.usage" };

  if (
    linksLimit.limitStatus.value == OVERAGE_STATUS.ALL_DISABLE ||
    linksLimit.limitStatus.value == OVERAGE_STATUS.BILLABLE_ENABLE
  ) {
    redirectPath = { name: "settings.invoices" }
  }

  router.push(redirectPath).finally(() => {
    closePagePopover();
    closeStaticPopover();
  });
}
</script>

<template>
  <TuFeedbackBanner
    class="limit-banner"
    :content="bannerContent"
    :feedback-button="bannerButton"
    :is-closable="false"
    @cta-click="onManageLimits"
  />
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.limit-banner {
  margin-bottom: spacing.$xxs;
}
</style>
